import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query'
import { useCallback, useEffect, useRef } from 'react'

import { useRealtimeEvent } from '@/lib/realtime'
import { useSession } from '@/lib/session'
import type { UUID } from '@/types/api'

import { chatApi } from './api'
import { chatKeys } from './keys'
import type { ChannelListResponse, ChatChannel, ChatMessage, MessageListResponse } from './types'

type MessagePages = InfiniteData<MessageListResponse, number | null>

/* ------------------------------------------------------------------ reads */

/** Every conversation the caller belongs to, ordered by recency. */
export function useChannels(slug: string | null) {
  return useQuery({
    queryKey: chatKeys.channels(slug ?? ''),
    queryFn: () => chatApi.listChannels(slug as string),
    enabled: Boolean(slug),
    staleTime: 15_000,
    // The realtime stream carries updates; this is the fallback for a tab
    // that never connected.
    refetchInterval: 60_000,
  })
}

/**
 * Unread messages across all conversations, for the sidebar badge.
 *
 * Shares the channels query, so mounting it in the sidebar costs nothing when
 * the chat page is open and one slow poll otherwise.
 */
export function useChatUnreadTotal(slug: string | null): number {
  const query = useQuery({
    queryKey: chatKeys.channels(slug ?? ''),
    queryFn: () => chatApi.listChannels(slug as string),
    enabled: Boolean(slug),
    staleTime: 15_000,
    refetchInterval: 60_000,
    select: (response: ChannelListResponse) => response.unread_total,
  })
  return query.data ?? 0
}

export function useChannel(slug: string | null, channelId: string | null) {
  const channels = useChannels(slug)
  const fromList = channels.data?.data.find((channel) => channel.id === channelId) ?? null
  const detail = useQuery({
    queryKey: chatKeys.channel(slug ?? '', channelId ?? ''),
    queryFn: () => chatApi.getChannel(slug as string, channelId as UUID),
    enabled: Boolean(slug && channelId) && !fromList,
    staleTime: 30_000,
  })
  return {
    channel: fromList ?? detail.data ?? null,
    isLoading: channels.isLoading || (!fromList && detail.isLoading),
    error: fromList ? null : detail.error,
  }
}

/**
 * Messages in one conversation, oldest first, paging backwards.
 *
 * `pages[0]` is the newest page; `fetchNextPage` loads an OLDER page. The
 * flattened list is what the view renders.
 */
export function useMessages(slug: string | null, channelId: string | null) {
  const query = useInfiniteQuery<MessageListResponse, Error, MessagePages, readonly unknown[], number | null>({
    queryKey: chatKeys.messages(slug ?? '', channelId ?? ''),
    queryFn: ({ pageParam }) =>
      chatApi.listMessages(slug as string, channelId as UUID, {
        before: pageParam ?? undefined,
        limit: 50,
      }),
    initialPageParam: null,
    getNextPageParam: (last) => last.next_before ?? undefined,
    enabled: Boolean(slug && channelId),
    staleTime: 30_000,
  })
  const messages: ChatMessage[] = query.data
    ? [...query.data.pages].reverse().flatMap((page) => page.data)
    : []
  return { ...query, messages }
}

/* ------------------------------------------------------------ cache edits */

function upsertMessage(pages: MessagePages | undefined, message: ChatMessage): MessagePages | undefined {
  if (!pages) return pages
  let replaced = false
  const next = pages.pages.map((page) => {
    const index = page.data.findIndex((existing) => existing.id === message.id)
    if (index === -1) return page
    replaced = true
    const data = page.data.slice()
    data[index] = message
    return { ...page, data }
  })
  if (!replaced) {
    const first = next[0]
    if (first) {
      next[0] = { ...first, data: [...first.data, message].sort((a, b) => a.id - b.id) }
    } else {
      next.push({ data: [message], next_before: null })
    }
  }
  return { ...pages, pages: next }
}

/* -------------------------------------------------------------- realtime */

/**
 * Keeps the chat caches current from the realtime stream. Mount once on the
 * chat page; the sidebar badge only needs the channel-list invalidation,
 * which the notification bell's own listener also triggers.
 */
export function useChatRealtime(slug: string | null, onChannelDeleted?: (channelId: string) => void) {
  const queryClient = useQueryClient()
  useRealtimeEvent<{ channel_id: string; message: ChatMessage }>('chat.message', (data) => {
    if (!slug) return
    queryClient.setQueryData<MessagePages>(chatKeys.messages(slug, data.channel_id), (pages) =>
      upsertMessage(pages, data.message),
    )
    void queryClient.invalidateQueries({ queryKey: chatKeys.channels(slug) })
  })
  useRealtimeEvent<{ channel_id: string; action: string }>('chat.channel', (data) => {
    if (!slug) return
    void queryClient.invalidateQueries({ queryKey: chatKeys.channels(slug) })
    void queryClient.invalidateQueries({ queryKey: chatKeys.channel(slug, data.channel_id) })
    if (data.action === 'deleted') onChannelDeleted?.(data.channel_id)
  })
}

/* ------------------------------------------------------------- mutations */

export function useChatMutations(slug: string | null) {
  const queryClient = useQueryClient()
  const { user } = useSession()
  const invalidateChannels = () =>
    queryClient.invalidateQueries({ queryKey: chatKeys.channels(slug ?? '') })

  const send = useMutation({
    mutationFn: ({ channelId, body }: { channelId: UUID; body: string }) =>
      chatApi.postMessage(slug as string, channelId, body),
    onMutate: async ({ channelId, body }) => {
      const key = chatKeys.messages(slug ?? '', channelId)
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<MessagePages>(key)
      // A negative id marks the optimistic row; the server's copy replaces it
      // on success or the realtime echo, whichever arrives first.
      const optimistic: ChatMessage = {
        id: -Date.now(),
        channel_id: channelId,
        author_id: user?.id ?? null,
        body,
        mentions: [],
        edited_at: null,
        deleted_at: null,
        created_at: new Date().toISOString(),
        author: user ? { id: user.id, name: user.name, email: user.email, avatar_color: null } : undefined,
      }
      queryClient.setQueryData<MessagePages>(key, (pages) =>
        upsertMessage(pages ?? { pages: [{ data: [], next_before: null }], pageParams: [null] }, optimistic),
      )
      return { key, previous, optimisticId: optimistic.id }
    },
    onError: (_error, _input, context) => {
      if (context) queryClient.setQueryData(context.key, context.previous)
    },
    onSuccess: (message, _input, context) => {
      queryClient.setQueryData<MessagePages>(context.key, (pages) => {
        if (!pages) return pages
        const stripped: MessagePages = {
          ...pages,
          pages: pages.pages.map((page) => ({
            ...page,
            data: page.data.filter((existing) => existing.id !== context.optimisticId),
          })),
        }
        return upsertMessage(stripped, message)
      })
      void invalidateChannels()
    },
  })

  const edit = useMutation({
    mutationFn: ({ messageId, body }: { messageId: number; channelId: UUID; body: string }) =>
      chatApi.editMessage(slug as string, messageId, body),
    onSuccess: (message, { channelId }) => {
      queryClient.setQueryData<MessagePages>(chatKeys.messages(slug ?? '', channelId), (pages) =>
        upsertMessage(pages, message),
      )
    },
  })

  const remove = useMutation({
    mutationFn: ({ messageId }: { messageId: number; channelId: UUID }) =>
      chatApi.deleteMessage(slug as string, messageId),
    onSuccess: (message, { channelId }) => {
      queryClient.setQueryData<MessagePages>(chatKeys.messages(slug ?? '', channelId), (pages) =>
        upsertMessage(pages, message),
      )
      void invalidateChannels()
    },
  })

  const createDm = useMutation({
    mutationFn: (userId: number) => chatApi.createDm(slug as string, userId),
    onSuccess: () => void invalidateChannels(),
  })

  const createGroup = useMutation({
    mutationFn: (input: { name: string; member_ids: number[] }) =>
      chatApi.createGroup(slug as string, input),
    onSuccess: () => void invalidateChannels(),
  })

  const rename = useMutation({
    mutationFn: ({ channelId, name }: { channelId: UUID; name: string }) =>
      chatApi.renameChannel(slug as string, channelId, name),
    onSuccess: (channel) => {
      queryClient.setQueryData<ChatChannel>(chatKeys.channel(slug ?? '', channel.id), channel)
      void invalidateChannels()
    },
  })

  const addMembers = useMutation({
    mutationFn: ({ channelId, userIds }: { channelId: UUID; userIds: number[] }) =>
      chatApi.addMembers(slug as string, channelId, userIds),
    onSuccess: (channel) => {
      queryClient.setQueryData<ChatChannel>(chatKeys.channel(slug ?? '', channel.id), channel)
      void invalidateChannels()
    },
  })

  const removeMember = useMutation({
    mutationFn: ({ channelId, userId }: { channelId: UUID; userId: number }) =>
      chatApi.removeMember(slug as string, channelId, userId),
    onSuccess: () => void invalidateChannels(),
  })

  const deleteChannel = useMutation({
    mutationFn: (channelId: UUID) => chatApi.deleteChannel(slug as string, channelId),
    onSuccess: () => void invalidateChannels(),
  })

  return { send, edit, remove, createDm, createGroup, rename, addMembers, removeMember, deleteChannel }
}

/**
 * Advances the read marker while a conversation is open.
 *
 * Debounced so a burst of arriving messages is one request, and keyed on the
 * newest id so re-renders with nothing new do nothing.
 */
export function useMarkRead(slug: string | null, channelId: string | null, newestId: number | null) {
  const queryClient = useQueryClient()
  const timer = useRef<number | null>(null)
  const lastSent = useRef<number>(0)

  const flush = useCallback(
    (id: number) => {
      if (!slug || !channelId || id <= 0 || id <= lastSent.current) return
      lastSent.current = id
      chatApi
        .markRead(slug, channelId as UUID, id)
        .then(() => {
          void queryClient.invalidateQueries({ queryKey: chatKeys.channels(slug) })
          // The bell counts DM/mention notifications until the chat is read.
          void queryClient.invalidateQueries({ queryKey: ['v2', 'notifications'] })
        })
        .catch(() => {
          lastSent.current = 0
        })
    },
    [slug, channelId, queryClient],
  )

  useEffect(() => {
    lastSent.current = 0
  }, [channelId])

  useEffect(() => {
    if (!newestId || newestId <= 0) return
    if (timer.current) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => flush(newestId), 400)
    return () => {
      if (timer.current) window.clearTimeout(timer.current)
    }
  }, [newestId, flush])
}
